// TRAIL LINES: the trail index as coloured lines in the 3D view — a zoom
// INSTRUMENT layer (ways-3d-coloured-lines, owner 2026-10-11). The plan, the
// colours, the fade and the drape rule are client/src/lineplan.js (pure);
// here: the draw and the drape scheduler. APP_PLAYER §5 "Trail lines".
//
//   layer    the FOCUS layer only (monoCam / scopeCam; the desktop / phone
//            main camera while the view is armed past view_arm.zoom_on):
//            the naked-eye pass and VR (headset pass AND zoom window: main.js
//            passes zoom 0, vr-nomap) never draw a line —
//            the world stays real (APP_PLAYER "Trail marks")
//   when     focus active AND fade > 0 (the gradings are always on — no
//            switch); otherwise the group is hidden and update() does no work
//   what     lineplan linePlan = farplan farPlan: the 2048 m chunks the wedge
//            reaches, nearest the apex first, under
//            renderconfig focus.tiers[t].trail_lines {tri_budget, draw_budget};
//            one draw per chunk
//   height   each point's y = terrain.groundDrawn(x, z) — the ground the
//            optic sees (the ground tree's node meshes, as drawn);
//            a point no resident node covers is not drawn. A drape PASS runs
//            over the admitted chunks nearest first when the tile set
//            (terrain.gen), the plan or the viewer (drape_step_m) moved, at
//            most every drape_ms, TIME-SLICED to drape_slice_ms a frame; a
//            chunk uploads once, when its last point is done
//   draw     one instanced screen-space quad per segment (2 tris): width in
//            px of the target (the Map's stroke width x px_1080 x the
//            target's height / 1080), pulled toward the camera along its own
//            view ray by pull_m + pull_k x d (the same pixel, a nearer
//            depth: a chord under a convex facet never buries, nothing
//            floats; a segment steeper than grade_max — a chord across a
//            drawn ring / focus tile step — is not drawn), faded in over
//            [near_m, near_full_m] from the eye
//            (your own path at your feet is the world's, not the
//            instrument's); transparent, no depth write, no fog, no tone
//            mapping — the Map's sRGB colour on screen
//   warm-up  ONE ShaderMaterial for every chunk, created with a keeper mesh
//            at construction (boot): warmShaders / warmLate / compileOptic /
//            preWarm compile its canvas and optic programs; never disposed
import * as THREE from 'three';
import { LINE_HIDE_Y, lineChunksJob, linePlan, drapePoints, lineFade } from './lineplan.js';
import { runSteps } from './decode.js';

const VERT = /* glsl */`
attribute vec3 iA;
attribute vec3 iB;
attribute vec4 iC;
attribute float iW;
uniform vec2 uRes;      // the target's viewport (px)
uniform float uPx;      // px per Map stroke px
uniform float uAlpha;   // the instrument's fade
uniform vec4 uPull;     // pull_m, pull_k, near_m, near_full_m
uniform vec2 uGrade;    // grade_max, grade_pad_m (lineplan.js lineSegShown)
varying vec4 vC;
void main() {
  vec4 a = modelViewMatrix * vec4(iA, 1.0);
  vec4 b = modelViewMatrix * vec4(iB, 1.0);
  // not drawn: a piece break, an end without drawn ground (or not draped
  // yet), a chord across a drawn step (steeper than grade_max: lineSegShown),
  // an end nearer than near_m (never behind the eye)
  if (iC.a < 0.002 || iA.y < -9.0e4 || iB.y < -9.0e4
    || abs(iB.y - iA.y) > uGrade.x * length(iB.xz - iA.xz) + uGrade.y
    || -a.z < uPull.z || -b.z < uPull.z) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    vC = vec4(0.0);
    return;
  }
  float da = length(a.xyz), db = length(b.xyz);
  float fa = smoothstep(uPull.z, uPull.w, da), fb = smoothstep(uPull.z, uPull.w, db);
  // the view-ray pull: same pixel, nearer depth (lineplan.js linePull)
  a.xyz *= 1.0 - min(0.5, (uPull.x + uPull.y * da) / da);
  b.xyz *= 1.0 - min(0.5, (uPull.x + uPull.y * db) / db);
  vec4 ca = projectionMatrix * a;
  vec4 cb = projectionMatrix * b;
  vec2 h = 0.5 * uRes;
  vec2 d = (cb.xy / cb.w - ca.xy / ca.w) * h;
  float L = length(d);
  vec2 t = L > 1e-4 ? d / L : vec2(1.0, 0.0);
  vec2 nrm = vec2(-t.y, t.x);
  bool atA = position.x < 0.5;
  vec4 c = atA ? ca : cb;
  c.xy += nrm * (position.y * 0.05 * iW * uPx) / h * c.w;
  gl_Position = c;
  vC = vec4(iC.rgb, iC.a * uAlpha * (atA ? fa : fb));
}
`;
const FRAG = /* glsl */`
varying vec4 vC;
void main() {
  // the Map's sRGB colour -> linear; colorspace_fragment returns it to the
  // target's space (sRGB on the canvas: the Map's bytes)
  vec3 c = vC.rgb;
  vec3 lin = mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c));
  gl_FragColor = vec4(lin, vC.a);
  #include <colorspace_fragment>
}
`;

const _vp = new THREE.Vector4();
const _clock = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export class TrailLines {
  // scene; look = renderconfig trail_lines (tol_m, step_m, chunk_m, px_1080,
  // pull_m, pull_k, near_m, near_full_m, grade_max, grade_pad_m, drape_ms,
  // drape_step_m, drape_slice_ms); tiers = renderconfig focus.tiers, tier = detailTier()
  // (no tiers[tier].trail_lines = off); layer = focus.layer
  constructor(scene, look, tiers, tier, layer) {
    this.look = look || null;
    this.cfg = (this.look && tiers && tiers[tier] && tiers[tier].trail_lines) || null;
    this.layer = layer;
    this.group = new THREE.Group();
    this.group.name = 'trail-lines';
    this.group.visible = false;
    scene.add(this.group);
    this.chunks = null;           // lineplan chunks (build)
    this.stats = null;
    this.plan = null;             // the last linePlan
    this.meshes = new Map();      // chunk -> mesh (built on first admission, kept)
    this.alpha = 0;
    this._gen = -1;
    this._building = false;
    // the drape pass (cursor over plan.list)
    this.drape = { list: [], cur: -1, k: 0, chg: false, ylo: Infinity, yhi: -Infinity, t: -1e9, gen: -1, planGen: -1,
      at: { x: 1e9, z: 1e9 }, passes: 0, ms: 0 };
    this._planGen = 0;
    if (!this.cfg) return;
    this.mat = new THREE.ShaderMaterial({
      name: 'trail-lines',
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uRes: { value: new THREE.Vector2(1920, 1080) },
        uPx: { value: 1 },
        uAlpha: { value: 0 },
        uPull: { value: new THREE.Vector4(this.look.pull_m, this.look.pull_k, this.look.near_m, this.look.near_full_m) },
        uGrade: { value: new THREE.Vector2(this.look.grade_max, this.look.grade_pad_m) },
      },
      transparent: true,
      depthWrite: false,
      depthTest: true,
      fog: false,
      toneMapped: false,
      side: THREE.DoubleSide,
      // ONE pass (as the water's, sky.js): three draws a transparent
      // DoubleSide material twice (back, then front faces) and bumps its
      // version twice a draw — a program re-resolve per chunk per frame
      // while the lines show. A screen-space quad faces either way by its
      // segment's screen direction; one pass draws the same quads in
      // instance order (only crossings of opposite-facing segments blend in
      // another order; depthWrite off)
      forceSinglePass: true,
    });
    // the keeper: in the scene from boot, never drawn, so every warm-up pass
    // compiles this material's programs before the first zoom
    const kp = new Float32Array([0, LINE_HIDE_Y, 0, 1, LINE_HIDE_Y, 0]);
    this.keeper = this._mesh({ pts: kp, col: new Uint8Array(4), wid: new Uint8Array(1), x0: 0, z0: 0, x1: 1, z1: 1 });
    this.keeper.visible = false;
    this.keeper.frustumCulled = false;
    this.group.add(this.keeper);
  }

  // one chunk's mesh: the unit quad instanced per segment (point k -> k + 1
  // of ONE interleaved point buffer: no duplicated points)
  _mesh(c) {
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute([0, -1, 0, 0, 1, 0, 1, -1, 0, 1, 1, 0], 3));
    geo.setIndex([0, 2, 1, 2, 3, 1]);
    const ib = new THREE.InstancedInterleavedBuffer(c.pts, 3, 1);
    geo.setAttribute('iA', new THREE.InterleavedBufferAttribute(ib, 3, 0));
    geo.setAttribute('iB', new THREE.InterleavedBufferAttribute(ib, 3, 3));
    geo.setAttribute('iC', new THREE.InstancedBufferAttribute(c.col, 4, true));
    geo.setAttribute('iW', new THREE.InstancedBufferAttribute(c.wid, 1, false));
    geo.instanceCount = c.pts.length / 3 - 1;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3((c.x0 + c.x1) / 2, 0, (c.z0 + c.z1) / 2),
      Math.hypot(c.x1 - c.x0, c.z1 - c.z0) / 2 + 4);
    const m = new THREE.Mesh(geo, this.mat);
    m.layers.set(this.layer);
    m.renderOrder = 50;
    m.matrixAutoUpdate = false;
    m.userData.ib = ib;
    m.onBeforeRender = this._onBefore;
    return m;
  }

  // per pass: the target's viewport -> resolution + stroke px
  _onBefore = (renderer) => {
    const vp = renderer.getCurrentViewport(_vp);
    const u = this.mat.uniforms;
    if (u.uRes.value.x !== vp.z || u.uRes.value.y !== vp.w) {
      u.uRes.value.set(vp.z, vp.w);
      u.uPx.value = this.look.px_1080 * vp.w / 1080;
      this.mat.uniformsNeedUpdate = true;
    }
  };

  // the chunk buffers from the trail index, a step generator over the
  // slicer (phase 1; never one long task). stations: Map(entry -> its
  // stations {x, z, kind}) captured BEFORE TrailMarks.build drops `st`
  // (an entry without stations draws its 2 m `pts`). -> Promise(stats)
  build(trails, stations) {
    if (!this.cfg || this._building || !trails || !trails.length) return Promise.resolve(null);
    this._building = true;
    const out = {};
    const job = lineChunksJob(trails, (e) => (stations && stations.get(e)) || null, this.look, out);
    return runSteps(job, 'trails.lines').then(() => {
      this.chunks = out.chunks;
      this.stats = out.stats;
      this._gen = -1;
      return out.stats;
    });
  }

  // every frame after focus.update / terrain.updateFocus. z = the view's
  // magnification for the fade (desktop / phone: viewZoom.z; an optic:
  // Infinity — the raised optic IS the instrument); viewer = the player position (the drape's re-sample distance)
  update(focus, terrain, z, zOn, zFull, viewer, now = _clock()) {
    if (!this.cfg || !this.chunks) return;
    const a = focus && focus.active ? lineFade(z, zOn, zFull) : 0;
    this.alpha = a;
    this.mat.uniforms.uAlpha.value = a;
    this.group.visible = a > 0;
    if (!(a > 0)) return;
    if (focus.gen !== this._gen) {
      this._gen = focus.gen;
      const plan = linePlan(this.chunks, focus, this.cfg);
      this.plan = plan;
      this._planGen++;
      const on = new Set([this.keeper]);
      for (const e of plan.list) {
        let m = this.meshes.get(e.chunk);
        if (!m) this.meshes.set(e.chunk, m = this._mesh(e.chunk));
        if (!m.parent) this.group.add(m);
        on.add(m);
      }
      for (const m of [...this.group.children]) if (!on.has(m)) this.group.remove(m);
    }
    this._drape(terrain, viewer, now);
  }

  // the drape pass (module comment): resumable, ~drape_slice_ms a frame
  _drape(terrain, viewer, now) {
    const d = this.drape, L = this.look;
    if (!terrain || !this.plan) return;
    if (d.cur < 0 || d.cur >= d.list.length) {
      const moved = Math.hypot(viewer.x - d.at.x, viewer.z - d.at.z) >= L.drape_step_m;
      if (!moved && terrain.gen === d.gen && this._planGen === d.planGen) return;
      if (now - d.t < L.drape_ms) return;
      d.t = now; d.gen = terrain.gen; d.planGen = this._planGen;
      d.at.x = viewer.x; d.at.z = viewer.z;
      d.list = this.plan.list.map((e) => e.chunk);
      d.cur = 0; d.k = 0; d.chg = false; d.ylo = Infinity; d.yhi = -Infinity;
      d.passes++;
    }
    const G = (x, z) => terrain.groundDrawn(x, z);
    const t0 = _clock();
    while (d.cur < d.list.length) {
      const c = d.list[d.cur];
      const r = drapePoints(c.pts, G, d.k, 256);
      d.k = r.next;
      d.chg = d.chg || r.changed;
      if (r.ylo < d.ylo) d.ylo = r.ylo;
      if (r.yhi > d.yhi) d.yhi = r.yhi;
      if (d.k * 3 >= c.pts.length) {
        const m = this.meshes.get(c);
        if (m) {
          if (d.chg) m.userData.ib.needsUpdate = true;
          if (d.yhi >= d.ylo) {
            const bs = m.geometry.boundingSphere;
            bs.center.y = (d.ylo + d.yhi) / 2;
            bs.radius = Math.hypot(c.x1 - c.x0, c.z1 - c.z0, d.yhi - d.ylo) / 2 + 4;
          }
        }
        d.cur++; d.k = 0; d.chg = false; d.ylo = Infinity; d.yhi = -Infinity;
      }
      if (_clock() - t0 >= L.drape_slice_ms) break;
    }
    d.ms += _clock() - t0;
  }
}
